import {apiRequest} from "../../../utils/AxiosHelper";
import APIs from "../../../modules/settings/APIs";
import {useAppSelector} from "../../../hooks/hooks";
import {useQuery, UseQueryOptions} from '@tanstack/react-query';
import {Process} from "../../../types";

/**
 * Flat shape returned by GET /processes/tree (one row per process node, ordered by path).
 * See ProcessBean.java in giro-parameters.
 */
interface ProcessRow {
  id: number;
  stableId: string;
  parentId: number | null;
  versionId: number;
  nodeType: 'PROCESS_TYPE' | 'PROCESS' | 'SUB_PROCESS' | 'ACTIVITY';
  depth: number;
  path: string;
  description?: string;
  abbreviation?: string;
  alias?: string;
  state?: string;
  tenantId?: string;
  nodeSubtype?: string;
  critic?: string;
  descriptionLong?: string;
  importance?: string;
  evaluator?: string;
}

interface ProcessParams {
  // Kept for API compatibility with old callers; ignored by the flat-list endpoint.
  node?: any;
  depth?: number;
}

/**
 * Map a flat row to the tree-shaped {@link Process} consumed by antd TreeSelect.
 * `value` = stableId so form submissions carry a UUID that {@code ProcessAssessmentController}
 * can resolve directly.
 */
const toProcessNode = (row: ProcessRow): Process => ({
  value: row.stableId,
  title: row.description ?? '',
  key: row.stableId,
  children: [],
  id: row.id,
  stableId: row.stableId,
  parentId: row.parentId,
  versionId: row.versionId,
  nodeType: row.nodeType,
  depth: row.depth,
  path: row.path,
  description: row.description,
  abbreviation: row.abbreviation,
  alias: row.alias,
  state: row.state,
  nodeSubtype: row.nodeSubtype,
  critic: row.critic,
  descriptionLong: row.descriptionLong,
  importance: row.importance,
  evaluator: row.evaluator,
  tenantId: row.tenantId,
  text: row.description,
});

/**
 * Fold a flat list (ordered by path) into a parent → children tree using parentId.
 * The path-ordering guarantees parents appear before children, but the algorithm
 * doesn't depend on it — it builds the lookup map first, then attaches each node
 * to its parent.
 */
const buildTree = (rows: ProcessRow[]): Process[] => {
  const byId = new Map<number, Process>();
  rows.forEach(row => {
    byId.set(row.id, toProcessNode(row));
  });

  const roots: Process[] = [];
  rows.forEach(row => {
    const node = byId.get(row.id)!;
    if (row.parentId == null) {
      roots.push(node);
    } else {
      const parent = byId.get(row.parentId);
      if (parent) {
        parent.children.push(node);
      } else {
        // Orphan (parent missing from result set) — surface at the root so it isn't lost.
        roots.push(node);
      }
    }
  });
  return roots;
};

const fetchProcesses = async (_params: ProcessParams): Promise<Process[]> => {
  const response = await apiRequest.get(APIs.parameters.common.process.tree);
  // ResponseBody envelope: { data: ProcessRow[], totalCount, success, message }
  const rows: ProcessRow[] = response.data?.data ?? [];
  return buildTree(rows);
};

export const useProcess = (params: ProcessParams = {}, queryOptions?: Partial<UseQueryOptions<Process[], Error>>) => {
  const {isAuthenticated, token} = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: ['processes', params],
    queryFn: () => fetchProcesses(params),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: isAuthenticated && !!token,
    ...queryOptions,
  });
};
